/** @jsx jsx */

import { useMemo, useState } from 'react';
import { Icon } from '@zapier/zinnia-icons';
import { Colors, Link, Text } from '@zapier/design-system';
import { css, jsx } from '@emotion/core';
import { BaseTile } from './BaseTile';
import {
  defaultInitialSelectedItem,
  timePeriodDaysItems,
  PartnerInteractionType,
  partnerEmbedTypeItems,
  escapeHTML,
  convertToPercentage,
} from '../utils';
import DateRangeDropdown from '../DateRangeDropdown';
import PartnerEmbedDropdown from '../PartnerEmbedDropdown';
import {
  EmbedReferralFunnelDatum,
  useQueryEmbedInsightsReferralFunnel,
} from 'app/developer-v3/hooks/useQueryEmbedInsightsReferralFunnel';
import { TimePeriodDays } from 'app/developer-v3/hooks/useIntegrationInsights';
import { CommonMultiItemsChart } from 'app/developer-v3/components/CommonChart/CommonMultiItemsChart';
import { sortBy } from 'lodash';
import { InfoIconTooltip } from 'app/developer-v3/components/InfoIconTooltip/InfoIconTooltip';
import { EmbedInsightsTileEmpty } from './EmbedInsightsTileEmpty';

const html = String.raw;

const styleRoot = css`
  display: flex;
  flex-direction: column;
  background-color: ${Colors.PrimeWhite};
  border-top: 1px solid ${Colors.GrayWarm4};
`;

const styleDropdownContainer = css`
  display: flex;
  flex: 0;
  gap: 16px;
`;

const styleTotalConversion = css`
  display: flex;
  flex-direction: column;
`;

const styleTileBody = css`
  display: grid;
  width: 100%;
  grid-template-columns: 79% 21%;
`;

interface EmbedInsightsTileProps {
  appId: string;
  dataTestId?: string;
}

export default function EmbedInsightsTile({
  appId,
  dataTestId,
}: EmbedInsightsTileProps) {
  const [hasRenderError, setHasRenderError] = useState(false);
  const [
    selectedPartnerInteractionType,
    setSelectedPartnerInteractionType,
  ] = useState<PartnerInteractionType>(PartnerInteractionType.all);
  const [selectedTimePeriod, setSelectedTimePeriod] = useState<TimePeriodDays>(
    TimePeriodDays.Last_Thirty_days
  );
  const { data, error } = useQueryEmbedInsightsReferralFunnel(appId);

  const percentZapsActivated = useMemo(() => {
    const datum = getDatumToDisplay({
      data,
      interactionType: selectedPartnerInteractionType,
      timePeriod: selectedTimePeriod,
    });
    if (!datum) {
      return NaN;
    }
    return datum.count_users_zap_activated / datum.count_referred_sessions;
  }, [data, selectedPartnerInteractionType, selectedTimePeriod]);

  const chartData = useMemo(() => {
    const datum = getDatumToDisplay({
      data,
      interactionType: selectedPartnerInteractionType,
      timePeriod: selectedTimePeriod,
    });
    return formatDatumForChart({
      datum,
      timePeriod: selectedTimePeriod,
    });
  }, [data, selectedPartnerInteractionType, selectedTimePeriod]);

  // NOTE: It's kind of weird to re-order these in the list but not label
  // them... Maybe we should `.map` over this also and prepend something to the
  // name...
  const typeHasData = new Map(
    partnerEmbedTypeItems.map(item => [
      item.value,
      !isDatumEmpty(
        getDatumToDisplay({
          data,
          interactionType: item.value,
          timePeriod: selectedTimePeriod,
        })
      ),
    ])
  );
  const dropdownItems = sortBy(partnerEmbedTypeItems, item => {
    // Array of values to sort by. Remember that sorting is done in ascending
    // order, so we give empty data a larger value than non-empty data so it
    // will be sorted to the end of the list.
    return [
      // Sort types with data before types without data
      typeHasData.get(item.value) ? 0 : 1,
      // Sort alphabetically by label
      item.label.toLowerCase(),
    ];
  }).map(item => {
    if (typeHasData.get(item.value)) {
      return item;
    }
    return {
      label: `${item.label} (no data)`,
      value: item.value,
    };
  });

  return (
    <div css={styleRoot}>
      <BaseTile
        dataTestId={dataTestId}
        dropdowns={[
          <div css={styleDropdownContainer}>
            <PartnerEmbedDropdown
              initialSelectedItem={
                dropdownItems.find(
                  item => item.value === selectedPartnerInteractionType
                ) ?? dropdownItems[0]
              }
              items={dropdownItems}
              // Recreate the dropdown when the labels change, since the
              // component doesn't support that directly
              key={dropdownItems.map(item => item.label).join(',')}
              onSelect={setSelectedPartnerInteractionType}
            />
            <DateRangeDropdown
              initialSelectedItem={defaultInitialSelectedItem}
              items={timePeriodDaysItems}
              onSelect={setSelectedTimePeriod}
            />
          </div>,
        ]}
        headerContent="How many users who click on Zap templates go on to activate Zaps"
        icon={<Icon name={'partnerEmbed'} size={24} />}
        isError={Boolean(error) || hasRenderError}
        // use the chart loader prop to handle display of loading state
        isLoading={false}
        // We have custom "no data" states in this component
        isNoData={false}
        style={{ height: '100%' }}
        tipContent={
          <Text type="Body1">
            <Link
              href="https://zapier.com/blog/guide-showcase-integration-customer-journey/"
              target="_blank"
            >
              Learn more
            </Link>{' '}
            about how to keep your users engaged with automation by showcasing
            your integration throughout the customer journey.
          </Text>
        }
        tipContentStyle={{ marginTop: 0 }}
        toolTipContent={
          <Text color="PrimeWhite" tag="p" type="Body1">
            How many users who click on Zap templates go on to have successful
            Zap task runs.
          </Text>
        }
        tooltipPosition="bottom end"
      >
        {typeHasData.get(selectedPartnerInteractionType) ? (
          <div css={styleTileBody}>
            <CommonMultiItemsChart
              chartType="ColumnChart"
              colors={['#40A6FF']}
              data={chartData}
              optionOverrides={{
                isStacked: true,
                // this hides the "Not converted" from the legend
                series: [{}, { visibleInLegend: false }],
                vAxis: { maxValue: 1, minValue: 0 },
              }}
              setHasRenderError={setHasRenderError}
              vAxisFormat="percent"
            />
            <div css={styleTotalConversion}>
              <Text margin="0 0 10px 0" type="Body4">
                Total conversion
              </Text>
              <Text type="SectionHeader">
                {!Number.isFinite(percentZapsActivated)
                  ? '-%'
                  : `${convertToPercentage(percentZapsActivated)}%`}
              </Text>
              <Text margin="0 0 20px">
                {
                  timePeriodDaysItems.find(
                    item => item.value === selectedTimePeriod
                  )?.label
                }
              </Text>
              <Text>
                From{' '}
                <Text type="Body3">
                  Users who clicked on Zap templates{' '}
                  <InfoIconTooltip
                    allowMultilineTooltip={true}
                    content="Unique users who clicked on Zap templates in embeds."
                    position="left"
                    size="20px"
                  />
                </Text>{' '}
                to{' '}
                <Text type="Body3">
                  Users who activated Zaps.{' '}
                  <InfoIconTooltip
                    allowMultilineTooltip={true}
                    content={
                      <dl css={{ display: 'grid' }}>
                        <Text color="PrimeWhite" tag="dt" type="Body3">
                          Users who created Zaps:
                        </Text>
                        <Text
                          color="GrayWarm3"
                          margin="0 0 20px 0"
                          tag="dd"
                          type="Body1"
                        >
                          Users who started on Zaps in the editor.
                        </Text>
                        <Text color="PrimeWhite" tag="dt" type="Body3">
                          Users who enabled Zaps:
                        </Text>
                        <Text
                          color="GrayWarm3"
                          margin="0 0 20px 0"
                          tag="dd"
                          type="Body1"
                        >
                          Users who created and successfully published Zaps.
                        </Text>
                        <Text color="PrimeWhite" tag="dt" type="Body3">
                          Users who activated Zaps:
                        </Text>
                        <Text
                          color="GrayWarm3"
                          margin="0 0 0 0"
                          tag="dd"
                          type="Body1"
                        >
                          Users who created Zaps that successfully ran tasks.
                        </Text>
                      </dl>
                    }
                    position="left"
                    size="20px"
                  />
                </Text>
              </Text>
            </div>
          </div>
        ) : (
          <EmbedInsightsTileEmpty
            appId={appId}
            interactionType={selectedPartnerInteractionType}
          />
        )}
      </BaseTile>
    </div>
  );
}

function getDatumToDisplay({
  data,
  interactionType,
  timePeriod,
}: {
  data: ReturnType<typeof useQueryEmbedInsightsReferralFunnel>['data'];
  interactionType: PartnerInteractionType;
  timePeriod: TimePeriodDays;
}): EmbedReferralFunnelDatum {
  if (!data || data.length < 0) {
    return undefined;
  }

  // Filter data to match the selected time period
  const dataPointsForTimePeriod = data.filter(
    datum => datum.timeframe_days === timePeriod
  );
  if (!dataPointsForTimePeriod || dataPointsForTimePeriod.length < 1) {
    return undefined;
  }

  // Within the selected time period, find data that matches the selected
  // partner interaction type
  if (interactionType !== PartnerInteractionType.all) {
    // If we're displaying a partner interaction type other than "all", then
    // there should be a single data point
    return dataPointsForTimePeriod.find(
      datum => datum.partner_interaction_type === interactionType
    );
  } else {
    // There isn't a single data point that represents "all" partner
    // interaction types. We have to construct one by summing the `count_*`
    // fields of all of the data points in the selected time period.
    const datum: EmbedReferralFunnelDatum = {
      ...dataPointsForTimePeriod[0],
      partner_interaction_type: 'all',
      count_referred_sessions: 0,
      count_user_signups: 0,
      count_users_zap_created: 0,
      count_users_zap_enabled: 0,
      count_users_zap_activated: 0,
    };
    const fields = [
      'count_referred_sessions',
      'count_user_signups',
      'count_users_zap_created',
      'count_users_zap_enabled',
      'count_users_zap_activated',
    ] as const;
    for (const item of dataPointsForTimePeriod) {
      for (const field of fields) {
        datum[field] += item[field];
      }
    }
    return datum;
  }
}

function isDatumEmpty(datum: EmbedReferralFunnelDatum | undefined): boolean {
  if (!datum) {
    return true;
  }
  return (
    datum.count_referred_sessions === 0 &&
    datum.count_user_signups === 0 &&
    datum.count_users_zap_created === 0 &&
    datum.count_users_zap_enabled === 0 &&
    datum.count_users_zap_activated === 0
  );
}

// Format data in a way that can be displayed by the chart component. Filter
// data based on the selected date range and the selected partner interaction
// type.
function formatDatumForChart({
  datum,
  timePeriod,
}: {
  datum: EmbedReferralFunnelDatum;
  timePeriod: TimePeriodDays;
}) {
  // Verify that we have a data point that matches the selected filters
  if (!datum) {
    return [];
  }

  // Zap templates click sessions data
  const numberConvertedZapTemplatesClickSessions =
    datum.count_referred_sessions;
  // equivalent to numberConvertedZapTemplatesClickSessions /
  // numberConvertedZapTemplatesClickSessions
  const percentConvertedZapTemplatesClickSessions = 1;
  const percentNotConvertedZapTemplatesClickSessions = 0;
  const topOfFunnelNumber = numberConvertedZapTemplatesClickSessions;
  const labelZapTemplatesClickSessions = 'Users who clicked on Zap templates';

  // Zapier sign-ups data
  const numberConvertedZapierSignUps = datum.count_user_signups;
  const numberNotConvertedZapierSignUps =
    numberConvertedZapTemplatesClickSessions - numberConvertedZapierSignUps;
  const percentConvertedZapierSignUps =
    numberConvertedZapierSignUps / topOfFunnelNumber;
  const percentNotConvertedZapierSignUps =
    numberNotConvertedZapierSignUps / topOfFunnelNumber;
  const labelZapierSignUps = 'Users who signed up for Zapier';

  // Zaps created data
  const numberConvertedZapsCreated = datum.count_users_zap_created;
  const numberNotConvertedZapsCreated =
    numberConvertedZapierSignUps - numberConvertedZapsCreated;
  const percentConvertedZapsCreated =
    numberConvertedZapsCreated / topOfFunnelNumber;
  const percentNotConvertedZapsCreated =
    numberNotConvertedZapsCreated / topOfFunnelNumber;
  const labelZapsCreated = 'Users who created Zaps';

  // Zaps enabled data
  const numberConvertedZapsEnabled = datum.count_users_zap_enabled;
  const numberNotConvertedZapsEnabled =
    numberConvertedZapsCreated - numberConvertedZapsEnabled;
  const percentConvertedZapsEnabled =
    numberConvertedZapsEnabled / topOfFunnelNumber;
  const percentNotConvertedZapsEnabled =
    numberNotConvertedZapsEnabled / topOfFunnelNumber;
  const labelZapsEnabled = 'Users who enabled Zaps';

  // Zaps activated data
  const numberConvertedZapsActivated = datum.count_users_zap_activated;
  const numberNotConvertedZapsActivated =
    numberConvertedZapsEnabled - numberConvertedZapsActivated;
  const percentConvertedZapsActivated =
    numberConvertedZapsActivated / topOfFunnelNumber;
  const percentNotConvertedZapsActivated =
    numberNotConvertedZapsActivated / topOfFunnelNumber;
  const labelZapsActivated = 'Users who activated Zaps';

  const columnStyleNotConverted = 'fill-opacity: 0.4';
  const tooltipHeader = {
    role: 'tooltip',
    type: 'string',
    p: {
      html: true,
    },
  };

  function generateTooltip({
    title,
    timeframe,
    amount,
    percentage,
  }: {
    title: string;
    timeframe: number;
    amount: number;
    percentage: number;
  }) {
    return html`
      <div style="width: 300px">
        <p>
          ${escapeHTML(title)} in the last ${escapeHTML(String(timeframe))}
          days: ${escapeHTML(new Intl.NumberFormat().format(amount))}
          (${escapeHTML(String(convertToPercentage(percentage)))}%)
        </p>
      </div>
    `;
  }

  const chartRows = [
    [
      'Conversion step',
      'All users',
      tooltipHeader,
      'Not converted',
      tooltipHeader,
      { role: 'style' },
    ],
    [
      labelZapTemplatesClickSessions,
      percentConvertedZapTemplatesClickSessions,
      generateTooltip({
        title: labelZapTemplatesClickSessions,
        timeframe: timePeriod,
        amount: numberConvertedZapTemplatesClickSessions,
        percentage: percentConvertedZapTemplatesClickSessions,
      }),
      percentNotConvertedZapTemplatesClickSessions,
      generateTooltip({
        title: labelZapTemplatesClickSessions,
        timeframe: timePeriod,
        amount: numberConvertedZapTemplatesClickSessions,
        percentage: percentNotConvertedZapTemplatesClickSessions,
      }),
      columnStyleNotConverted,
    ],
    [
      labelZapierSignUps,
      percentConvertedZapierSignUps,
      generateTooltip({
        title: labelZapierSignUps,
        timeframe: timePeriod,
        amount: numberConvertedZapierSignUps,
        percentage: percentConvertedZapierSignUps,
      }),
      percentNotConvertedZapierSignUps,
      generateTooltip({
        title:
          'Users who clicked on Zap templates but did not sign up for Zapier',
        timeframe: timePeriod,
        amount: numberNotConvertedZapierSignUps,
        percentage: percentNotConvertedZapierSignUps,
      }),
      columnStyleNotConverted,
    ],
    [
      labelZapsCreated,
      percentConvertedZapsCreated,
      generateTooltip({
        title: labelZapsCreated,
        timeframe: timePeriod,
        amount: numberConvertedZapsCreated,
        percentage: percentConvertedZapsCreated,
      }),
      percentNotConvertedZapsCreated,
      generateTooltip({
        title: 'Users who signed up for Zapier but did not create Zaps',
        timeframe: timePeriod,
        amount: numberNotConvertedZapsCreated,
        percentage: percentNotConvertedZapsCreated,
      }),
      columnStyleNotConverted,
    ],
    [
      labelZapsEnabled,
      percentConvertedZapsEnabled,
      generateTooltip({
        title: labelZapsEnabled,
        timeframe: timePeriod,
        amount: numberConvertedZapsEnabled,
        percentage: percentConvertedZapsEnabled,
      }),
      percentNotConvertedZapsEnabled,
      generateTooltip({
        title: 'Users who created Zaps but did not enable Zaps',
        timeframe: timePeriod,
        amount: numberNotConvertedZapsEnabled,
        percentage: percentNotConvertedZapsEnabled,
      }),
      columnStyleNotConverted,
    ],
    [
      labelZapsActivated,
      percentConvertedZapsActivated,
      generateTooltip({
        title: labelZapsActivated,
        timeframe: timePeriod,
        amount: numberConvertedZapsActivated,
        percentage: percentConvertedZapsActivated,
      }),
      percentNotConvertedZapsActivated,
      generateTooltip({
        title: 'Users who enabled Zaps but did not activate Zaps',
        timeframe: timePeriod,
        amount: numberNotConvertedZapsActivated,
        percentage: percentNotConvertedZapsActivated,
      }),
      columnStyleNotConverted,
    ],
  ];
  return chartRows;
}

export function useEmbedConversionPercentage({ appId }: { appId: ID }): number {
  const { data } = useQueryEmbedInsightsReferralFunnel(String(appId));
  const datum = getDatumToDisplay({
    data,
    interactionType: PartnerInteractionType.all,
    timePeriod: TimePeriodDays.Last_Ninety_days,
  });
  if (!datum) {
    return NaN;
  }
  return 100 * (datum.count_user_signups / datum.count_referred_sessions);
}
